import { useLazyQuery } from '@apollo/client';
import { Box, Button, MenuItem, TextField } from '@mui/material';
import type { useFormik } from 'formik';
import { matchIsValidColor } from 'mui-color-input';
import { useEffect, type ComponentProps } from 'react';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { boolean, number, object, ref, string } from 'yup';
import { DetailColorInput } from '~/components/forms/DetailColorInput';
import {
  DetailSelect,
  DetailSwitch,
  DetailTextArea,
  DetailTextField,
} from '~/components/forms/details';
import { DurationSlider } from '~/components/inputs';
import { DurationInput } from '~/components/inputs/DurationInput/DurationInput';
import { TooltipMenuItem, focusableDisabledItems } from '~/components/menu-item';
import {
  SettingDescription,
  SettingFieldRequired,
  SettingInputWrapper,
  SettingLabelWrapper,
  SettingName,
  SettingsBody,
  SettingsData,
  SettingsHead,
} from '~/components/settings';
import { useAppContext } from '~/contexts';
import { assert } from '~/lib/assert';
import { toDataUri } from '~/lib/file';
import { i18n } from '~/lib/i18n';
import { duration } from '~/lib/validators';
import type { NetworkEventTypesLeague } from '../queries/leagues.generated';
import { NetworkTeamsDocument } from '../queries/teams.generated';

export interface EventTypeFormValues {
  color: string;
  contentStartTime: string;
  contentStopTime: string;
  name: string;
  description: string | undefined;
  fcLeagueId: number;
  fcTeamId: number;
  iconInputUri: string;
  iconUploadUri: string;
  manageDisplay: boolean;
  sponsorStartTime: string;
  sponsorStopTime: string;
  syncFcAdmin: boolean;
}

export const eventTypeSchema = object({
  color: string()
    .required()
    .test('is-color', i18n.t('errors.validation.color'), (value) => matchIsValidColor(value))
    .label(i18n.t('pages.event_types.form.fields.color.label')),
  contentStartTime: duration({ min: { minutes: 0 } }).required(),
  contentStopTime: duration({ min: { minutes: 0 } }).required(),
  description: string().optional(),
  fcLeagueId: number().optional().label('FanConnect League'),
  fcTeamId: number()
    .label('FanConnect Team')
    .test({
      name: 'required-if-league-selected',
      exclusive: true,
      message: i18n.t('pages.event_types.form.validation.team_required_with_league'),
      test: function (value) {
        const fcLeagueId = this.resolve(ref('fcLeagueId'));
        return !(fcLeagueId !== -1 && value === -1);
      },
    }),
  iconUploadUri: string().trim(),
  iconInputUri: string().trim(),
  manageDisplay: boolean().required(),
  name: string().required(i18n.t('common.validation.name_required')).label('Name'),
  sponsorStartTime: duration({ min: { minutes: 0 }, max: { hours: 5 } }).required(),
  sponsorStopTime: duration({ min: { minutes: 0 }, max: { hours: 5 } }).required(),
  syncFcAdmin: boolean().required(),
});

export const serializeEventType = (values: EventTypeFormValues) => {
  const { fcLeagueId, fcTeamId, iconInputUri, iconUploadUri, ...rest } =
    eventTypeSchema.cast(values);

  assert(
    !iconInputUri || !iconUploadUri,
    'one or both of iconInputUri and iconUploadUri should be empty',
  );

  return {
    ...rest,
    fcLeagueId: fcLeagueId === -1 || fcLeagueId == null ? null : fcLeagueId,
    fcTeamId: fcTeamId === -1 || fcTeamId == null ? null : fcTeamId,
    iconUri: iconInputUri || iconUploadUri || undefined,
  };
};

export interface EventTypeFormProps<T extends EventTypeFormValues>
  extends Omit<ComponentProps<'form'>, 'onSubmit'> {
  formik: ReturnType<typeof useFormik<T>>;
  formId: string;
  iconFileUri?: string | undefined;
  leagues: readonly NetworkEventTypesLeague[];
  newRecord?: boolean;
}

export const EventTypeForm = <T extends EventTypeFormValues>({
  formik,
  formId,
  iconFileUri,
  leagues,
  newRecord = false,
}: EventTypeFormProps<T>) => {
  const { t } = useTranslation();
  const { currentNetwork } = useAppContext();
  const canAccessFcAdminLeagues = currentNetwork.canAccessFcAdminLeagues.value;

  const { getInputProps, open: selectFile } = useDropzone({
    accept: 'image/*',
    multiple: false,
    onDrop: async (accepted, _rejected, _event) => {
      if (accepted.length !== 1) return;
      const iconUploadUri = await toDataUri(accepted[0]);
      await formik.setFieldValue('iconUploadUri', iconUploadUri, false);
      await formik.setFieldValue('iconInputUri', '', true);
    },
  });

  const imgSrc =
    formik.values.iconInputUri.trim() || formik.values.iconUploadUri.trim() || iconFileUri;

  const [teamsQuery, { data }] = useLazyQuery(NetworkTeamsDocument);

  useEffect(() => {
    void teamsQuery({
      variables: { networkId: currentNetwork.id, leagueId: String(formik.values.fcLeagueId) },
    });
  }, [currentNetwork.id, formik.values.fcLeagueId, teamsQuery]);

  const teams = data?.network?.teams;

  const handleTeamChange = async (value: number) => {
    await formik.setFieldValue('fcTeamId', value, true);

    if (value === -1) {
      await formik.setFieldValue('syncFcAdmin', false, true);
      return;
    }

    const selectedTeam = teams?.find((team) => team.id === value);

    if (!selectedTeam || !selectedTeam.logoUrl) return;

    await formik.setFieldValue('iconUploadUri', selectedTeam.logoUrl);

    await formik.setFieldValue('color', `#${selectedTeam.primaryColor}`);

    await formik.setFieldValue('syncFcAdmin', true, true);
  };

  const handleLeagueChange = async (value: number) => {
    await formik.setFieldValue('fcLeagueId', value, true);

    // Get list of teams for selected league
    await teamsQuery({
      variables: {
        networkId: currentNetwork.id,
        leagueId: String(value),
      },
    });

    // Reset team and syncFcAdmin
    await handleTeamChange(-1);
  };

  return (
    <Box
      component="form"
      id={formId}
      onSubmit={formik.handleSubmit}
      sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
    >
      <div>
        <SettingsHead>{t('common.labels.general')}</SettingsHead>
        <SettingsBody>
          <SettingsData>
            <SettingLabelWrapper>
              <SettingName>
                {t('common.labels.name')}
                <SettingFieldRequired>*</SettingFieldRequired>
              </SettingName>
              <SettingDescription>
                {t('pages.event_types.form.fields.name.description')}
              </SettingDescription>
            </SettingLabelWrapper>
            <SettingInputWrapper>
              <DetailTextField
                autoFocus
                disabled={formik.isSubmitting}
                error={formik.touched.name && !!formik.errors.name}
                fullWidth
                helperText={(formik.touched.name && formik.errors.name) || ' '}
                name="name"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.name}
              />
            </SettingInputWrapper>
          </SettingsData>

          <SettingsData>
            <SettingLabelWrapper>
              <SettingName>{t('common.labels.description')}</SettingName>
              <SettingDescription>
                {t('pages.event_types.form.fields.description.description')}
              </SettingDescription>
            </SettingLabelWrapper>
            <SettingInputWrapper>
              <DetailTextArea
                disabled={formik.isSubmitting}
                error={formik.touched.description && !!formik.errors.description}
                fullWidth
                helperText={(formik.touched.description && formik.errors.description) || ' '}
                name="description"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.description}
              />
            </SettingInputWrapper>
          </SettingsData>

          <SettingsData>
            <SettingLabelWrapper>
              <SettingName>
                {t('pages.event_types.form.fields.color.label')}
                <SettingFieldRequired>*</SettingFieldRequired>
              </SettingName>
              <SettingDescription>
                {t('pages.event_types.form.fields.color.description')}
              </SettingDescription>
            </SettingLabelWrapper>
            <SettingInputWrapper>
              <DetailColorInput
                error={formik.touched.color && !!formik.errors.color}
                format="hex"
                helperText={(formik.touched.color && formik.errors.color) || ' '}
                name="color"
                onBlur={formik.handleBlur}
                onChange={(color) => formik.setFieldValue('color', color, true)}
                value={formik.values.color}
              />
            </SettingInputWrapper>
          </SettingsData>

          <SettingsData>
            <SettingLabelWrapper>
              <SettingName>
                {t('pages.event_types.form.fields.icon.label')}
                <SettingFieldRequired>*</SettingFieldRequired>
              </SettingName>
              <SettingDescription>
                {t('pages.event_types.form.fields.icon.description')}
              </SettingDescription>
            </SettingLabelWrapper>
            <SettingInputWrapper>
              <Box display="flex" flexDirection="row" gap={2}>
                {imgSrc && (
                  <Box
                    alt={t('pages.event_types.form.fields.icon.preview_alt')}
                    component="img"
                    display="flex"
                    src={imgSrc}
                    sx={{ height: '4rem' }}
                  />
                )}
                <Box display="flex" flexDirection="column" flexGrow={1} gap={2}>
                  <input {...getInputProps()} />
                  <div>
                    <Button onClick={selectFile} variant="outlined">
                      {t('pages.event_types.form.fields.icon.upload_button')}
                    </Button>
                  </div>
                  <Box display="flex" flexDirection="column">
                    <span>{t('pages.event_types.form.fields.icon.url_label')}</span>
                    <TextField
                      inputProps={{
                        'aria-label': t('pages.event_types.form.fields.icon.url_label'),
                      }}
                      error={
                        (formik.touched.iconInputUri || formik.touched.iconUploadUri) &&
                        !!formik.errors.iconInputUri
                      }
                      fullWidth
                      helperText={
                        ((formik.touched.iconInputUri || formik.touched.iconUploadUri) &&
                          formik.errors.iconInputUri) ||
                        ' '
                      }
                      name="iconInputUri"
                      onBlur={formik.handleBlur}
                      onChange={(event) => {
                        if (event.target.value.trim())
                          void formik.setFieldValue('iconUploadUri', '', false);
                        formik.handleChange(event);
                      }}
                      value={formik.values.iconInputUri}
                    />
                  </Box>
                </Box>
              </Box>
            </SettingInputWrapper>
          </SettingsData>

          <SettingsData>
            <SettingLabelWrapper>
              <SettingName>{t('pages.event_types.form.fields.league.label')}</SettingName>
              <SettingDescription>
                {t('pages.event_types.form.fields.league.description')}
              </SettingDescription>
            </SettingLabelWrapper>
            <SettingInputWrapper>
              <DetailSelect
                disabled={!newRecord}
                MenuProps={{ MenuListProps: focusableDisabledItems }}
                name="fcLeagueId"
                onBlur={formik.handleBlur}
                onChange={(event) => handleLeagueChange(parseInt(String(event.target.value)))}
                value={formik.values.fcLeagueId}
              >
                <MenuItem value={-1}>
                  {t('pages.event_types.form.fields.league.placeholder')}
                </MenuItem>
                {[...leagues]
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((league, index) => (
                    <TooltipMenuItem
                      key={index}
                      value={league.id}
                      disabled={!canAccessFcAdminLeagues}
                      tooltip={
                        !canAccessFcAdminLeagues &&
                        t('pages.event_types.form.fields.league.fc_admin_disabled')
                      }
                    >
                      <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            height: '25px',
                            width: '30px',
                          }}
                        >
                          {league.logoUrl && (
                            <img alt="" src={league.logoUrl} style={{ width: '25px' }} />
                          )}
                        </Box>
                        <Box>{league.name}</Box>
                      </Box>
                    </TooltipMenuItem>
                  ))}
              </DetailSelect>
            </SettingInputWrapper>
          </SettingsData>

          <SettingsData>
            <SettingLabelWrapper>
              <SettingName>{t('pages.event_types.form.fields.team.label')}</SettingName>
              <SettingDescription>
                {t('pages.event_types.form.fields.team.description')}
              </SettingDescription>
            </SettingLabelWrapper>
            <SettingInputWrapper>
              <DetailSelect
                name="fcTeamId"
                disabled={!newRecord || formik.values.fcLeagueId === -1}
                onBlur={formik.handleBlur}
                onChange={(event) => handleTeamChange(parseInt(String(event.target.value)))}
                value={formik.values.fcTeamId}
              >
                <MenuItem value={-1}>
                  {t('pages.event_types.form.fields.team.placeholder')}
                </MenuItem>
                {teams?.map((team, index) => (
                  <MenuItem key={index} value={team.id}>
                    <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'center',
                          height: '25px',
                          width: '30px',
                        }}
                      >
                        {team.logoUrl && (
                          <img alt="" src={team.logoUrl} style={{ width: '25px' }} />
                        )}
                      </Box>
                      <Box>
                        {team.location} {team.name}
                      </Box>
                    </Box>
                  </MenuItem>
                ))}
              </DetailSelect>
            </SettingInputWrapper>
          </SettingsData>

          <SettingsData className="last">
            <SettingLabelWrapper>
              <SettingName>{t('pages.event_types.form.fields.sync.label')}</SettingName>
              <SettingDescription>
                {t('pages.event_types.form.fields.sync.description')}
              </SettingDescription>
            </SettingLabelWrapper>
            <SettingInputWrapper>
              <DetailSwitch
                checked={formik.values.syncFcAdmin}
                color="primary"
                disabled={formik.isSubmitting || formik.values.fcLeagueId === -1}
                name="syncFcAdmin"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
              />
            </SettingInputWrapper>
          </SettingsData>
        </SettingsBody>
      </div>

      <div>
        <SettingsHead>{t('pages.event_types.form.sections.content_times')}</SettingsHead>
        <SettingsBody>
          <SettingsData>
            <SettingLabelWrapper>
              <SettingName>
                {t('pages.event_types.form.fields.content_start_time.label')}
              </SettingName>
              <SettingDescription>
                {t('pages.event_types.form.fields.content_start_time.description')}
              </SettingDescription>
            </SettingLabelWrapper>
            <SettingInputWrapper>
              <DurationInput
                disabled={formik.isSubmitting}
                name="contentStartTime"
                value={formik.values.contentStartTime}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
              />
            </SettingInputWrapper>
          </SettingsData>

          <SettingsData>
            <SettingLabelWrapper>
              <SettingName>
                {t('pages.event_types.form.fields.content_stop_time.label')}
              </SettingName>
              <SettingDescription>
                {t('pages.event_types.form.fields.content_stop_time.description')}
              </SettingDescription>
            </SettingLabelWrapper>
            <SettingInputWrapper>
              <DurationInput
                disabled={formik.isSubmitting}
                name="contentStopTime"
                value={formik.values.contentStopTime}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
              />
            </SettingInputWrapper>
          </SettingsData>

          <SettingsData className="last">
            <SettingLabelWrapper>
              <SettingName>{t('pages.event_types.form.fields.manage_display.label')}</SettingName>
              <SettingDescription>
                {t('pages.event_types.form.fields.manage_display.description')}
              </SettingDescription>
            </SettingLabelWrapper>
            <SettingInputWrapper>
              <DetailSwitch
                checked={formik.values.manageDisplay}
                color="primary"
                disabled={formik.isSubmitting}
                name="manageDisplay"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
              />
            </SettingInputWrapper>
          </SettingsData>
        </SettingsBody>
      </div>

      <div>
        <SettingsHead>{t('pages.event_types.form.sections.sponsor_times')}</SettingsHead>
        <SettingsBody>
          <SettingsData>
            <SettingLabelWrapper>
              <SettingName>
                {t('pages.event_types.form.fields.sponsor_start_time.label')}
              </SettingName>
              <SettingDescription>
                {t('pages.event_types.form.fields.sponsor_start_time.description')}
              </SettingDescription>
            </SettingLabelWrapper>
            <SettingInputWrapper>
              <DurationSlider
                disabled={formik.isSubmitting}
                max={{ hours: 5 }}
                min={{ minutes: 0 }}
                name="sponsorStartTime"
                required
                step={{ minutes: 15 }}
                value={formik.values.sponsorStartTime}
                onChange={formik.handleChange}
              />
            </SettingInputWrapper>
          </SettingsData>

          <SettingsData className="last">
            <SettingLabelWrapper>
              <SettingName>
                {t('pages.event_types.form.fields.sponsor_stop_time.label')}
              </SettingName>
              <SettingDescription>
                {t('pages.event_types.form.fields.sponsor_stop_time.description')}
              </SettingDescription>
            </SettingLabelWrapper>
            <SettingInputWrapper>
              <DurationSlider
                disabled={formik.isSubmitting}
                max={{ hours: 5 }}
                min={{ minutes: 0 }}
                name="sponsorStopTime"
                required
                step={{ minutes: 15 }}
                value={formik.values.sponsorStopTime}
                onChange={formik.handleChange}
              />
            </SettingInputWrapper>
          </SettingsData>
        </SettingsBody>
      </div>
    </Box>
  );
};
